import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Loader2, Sparkles, Check, AlertTriangle, XCircle,
  ShieldCheck, Rocket, Upload, Apple, Play, CheckCircle2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { publishingApi, APPLE_TRACKS, GOOGLE_TRACKS } from "@/lib/publishingApi";

const STEPS = ["Connect", "Store Listing", "Validation", "Build", "Release"];

const statusIcon = { passed: CheckCircle2, warning: AlertTriangle, action_required: XCircle };
const statusColor = { passed: "text-emerald-400", warning: "text-amber-400", action_required: "text-red-400" };

function Field({ label, value, onChange, testId, textarea, placeholder }) {
  const Cmp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</span>
      <Cmp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={textarea ? 4 : undefined}
        className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#0055FF] transition-colors duration-300 resize-none"
        data-testid={testId}
      />
    </label>
  );
}

export default function PublishWizard() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [listing, setListing] = useState({});
  const [step, setStep] = useState(0);
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState("");
  const [validation, setValidation] = useState(null);
  const [build, setBuild] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [rejection, setRejection] = useState({ message: "", result: null });
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([publishingApi.getProject(projectId), publishingApi.listConnections()]);
    setProject(p);
    setListing(p.listing || {});
    setConnections(c);
  }, [projectId]);

  useEffect(() => { load().catch(() => toast.error("Project not found")); }, [load]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const set = (k, v) => setListing((l) => ({ ...l, [k]: v }));

  const saveListing = async () => {
    await publishingApi.updateProject(projectId, { listing });
  };

  const runAI = async (tool) => {
    setBusy(tool);
    try {
      const data = await publishingApi.ai(projectId, tool);
      if (tool === "description") {
        setListing((l) => ({ ...l, short_description: data.short_description, full_description: data.full_description }));
      } else if (tool === "keywords") {
        set("keywords", data.keywords);
      } else if (tool === "category") {
        set("category", data.category);
      } else if (tool === "privacy") {
        set("privacy_url", `https://arcacentum.ai/legal/${listing.bundle_id || "app"}/privacy`);
        toast.success("Privacy Policy generated & hosted link set");
      } else if (tool === "terms") {
        set("terms_url", `https://arcacentum.ai/legal/${listing.bundle_id || "app"}/terms`);
        toast.success("Terms of Service generated & hosted link set");
      }
      toast.success("AI generated content added");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const uploadImage = (key, multiple) => (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (multiple) setListing((l) => ({ ...l, screenshots: [...(l.screenshots || []), reader.result] }));
        else set(key, reader.result);
      };
      reader.readAsDataURL(f);
    });
  };

  const runValidation = async () => {
    setBusy("validate");
    await saveListing();
    try {
      setValidation(await publishingApi.validate(projectId));
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const startBuild = async (platform) => {
    setBusy("build");
    try {
      const b = await publishingApi.build(projectId, platform);
      setBuild(b);
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const s = await publishingApi.getBuild(b.id);
        setBuild(s);
        if (s.stage === "Complete") clearInterval(pollRef.current);
      }, 1500);
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const doSubmit = async (platform, track) => {
    setBusy("submit");
    try {
      const s = await publishingApi.submit(projectId, { platform, track, build_id: build?.id });
      setSubmission(s);
      toast.success(`Submitted to ${platform === "ios" ? "App Store" : "Google Play"} (${track})`);
      await load();
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  const runRejection = async () => {
    if (!rejection.message.trim()) return;
    setBusy("reject");
    try {
      const r = await publishingApi.rejectionAssist(projectId, rejection.message);
      setRejection((x) => ({ ...x, result: r }));
    } catch (e) { toast.error(e.message); }
    setBusy("");
  };

  if (!project) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;
  }

  const platform = project.platform === "both" ? "ios" : project.platform;
  const canNext = step !== 2 || (validation && validation.can_submit);

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 right-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[140px] rounded-full" />
      <header className="relative z-10 max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="pub-logo">
          <div className="w-9 h-9 rounded-xl bg-[#0055FF] flex items-center justify-center font-display font-extrabold text-lg">A</div>
          <span className="font-display text-xl font-bold">ArcaCentum<span className="text-[#00F0FF]">.ai</span></span>
        </Link>
        <Link to="/publish" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2" data-testid="pub-dashboard-link">
          <ArrowLeft size={14} /> Dashboard
        </Link>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 pb-24">
        <div className="mb-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#00F0FF]">
          <Rocket size={14} /> One-Click App Launch
        </div>
        <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">{project.name}</h1>
        <p className="text-amber-400/80 text-xs mt-3 bg-amber-400/10 border border-amber-400/20 rounded-lg px-3 py-2 inline-block" data-testid="pub-demo-banner">
          Demo mode: builds & store submissions are simulated. Apple & Google still review every real submission — approval is never guaranteed or instant.
        </p>

        {/* Stepper */}
        <div className="flex items-center gap-2 mt-8 mb-10 flex-wrap" data-testid="pub-stepper">
          {STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => i <= step && setStep(i)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full font-mono text-xs transition-colors duration-300 ${
                i === step ? "bg-[#0055FF] text-white" : i < step ? "bg-white/10 text-white" : "text-slate-500 border border-white/10"
              }`}
              data-testid={`pub-step-${i}`}
            >
              <span className="w-5 h-5 rounded-full bg-black/30 flex items-center justify-center">{i < step ? <Check size={12} /> : i + 1}</span>
              {s}
            </button>
          ))}
        </div>

        {/* STEP 0 — Connect */}
        {step === 0 && (
          <div className="space-y-6" data-testid="pub-connect">
            <p className="text-slate-400">Securely connect your own developer accounts. Credentials are encrypted at rest and never exposed.</p>
            <div className="grid md:grid-cols-2 gap-4">
              {[{ p: "apple", icon: Apple, label: "Apple Developer" }, { p: "google", icon: Play, label: "Google Play Developer" }].map(({ p, icon: Icon, label }) => {
                const conn = connections.find((c) => c.provider === p);
                return (
                  <div key={p} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                    <div className="flex items-center gap-3 mb-4"><Icon size={20} /> <span className="font-display font-bold">{label}</span></div>
                    {conn ? (
                      <div className="flex items-center justify-between">
                        <span className="text-emerald-400 text-sm inline-flex items-center gap-2" data-testid={`pub-conn-${p}`}><CheckCircle2 size={15} /> {conn.account_name}</span>
                        <button onClick={async () => { await publishingApi.disconnect(conn.id); load(); }} className="text-xs text-slate-500 hover:text-red-400" data-testid={`pub-disconnect-${p}`}>Disconnect</button>
                      </div>
                    ) : (
                      <ConnectForm provider={p} onDone={load} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 1 — Listing */}
        {step === 1 && (
          <div className="space-y-5" data-testid="pub-listing">
            <div className="flex flex-wrap gap-2">
              {[["description", "Generate description"], ["keywords", "Generate keywords"], ["category", "Suggest category"], ["privacy", "Generate Privacy Policy"], ["terms", "Generate Terms"]].map(([t, label]) => (
                <button key={t} onClick={() => runAI(t)} disabled={busy === t}
                  className="inline-flex items-center gap-1.5 text-xs font-mono border border-[#0055FF]/30 text-[#00F0FF] rounded-full px-3 py-2 hover:bg-[#0055FF]/10 transition-colors duration-300 disabled:opacity-40"
                  data-testid={`pub-ai-${t}`}>
                  {busy === t ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} {label}
                </button>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="App name" value={listing.name} onChange={(v) => set("name", v)} testId="pub-field-name" />
              <Field label="Category" value={listing.category} onChange={(v) => set("category", v)} testId="pub-field-category" />
              <Field label="Short description" value={listing.short_description} onChange={(v) => set("short_description", v)} testId="pub-field-short" />
              <Field label="Keywords" value={listing.keywords} onChange={(v) => set("keywords", v)} testId="pub-field-keywords" />
            </div>
            <Field label="Full description" value={listing.full_description} onChange={(v) => set("full_description", v)} textarea testId="pub-field-full" />
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Privacy Policy URL" value={listing.privacy_url} onChange={(v) => set("privacy_url", v)} testId="pub-field-privacy" placeholder="https://" />
              <Field label="Terms URL" value={listing.terms_url} onChange={(v) => set("terms_url", v)} testId="pub-field-terms" placeholder="https://" />
              <Field label="Support URL" value={listing.support_url} onChange={(v) => set("support_url", v)} testId="pub-field-support" placeholder="https://" />
              <Field label="Contact email" value={listing.contact_email} onChange={(v) => set("contact_email", v)} testId="pub-field-contact" />
              <Field label="Bundle / Package ID" value={listing.bundle_id} onChange={(v) => set("bundle_id", v)} testId="pub-field-bundle" />
              <Field label="Pricing" value={listing.pricing} onChange={(v) => set("pricing", v)} testId="pub-field-pricing" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">App icon</span>
                <label className="mt-1.5 flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-white/15 px-4 py-3 hover:border-[#0055FF] transition-colors duration-300" data-testid="pub-icon-upload">
                  <Upload size={16} className="text-slate-400" />
                  <span className="text-sm text-slate-400">{listing.icon ? "Icon added ✓" : "Upload icon"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage("icon", false)} />
                </label>
              </div>
              <div>
                <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Screenshots</span>
                <label className="mt-1.5 flex items-center gap-3 cursor-pointer rounded-xl border border-dashed border-white/15 px-4 py-3 hover:border-[#0055FF] transition-colors duration-300" data-testid="pub-screenshots-upload">
                  <Upload size={16} className="text-slate-400" />
                  <span className="text-sm text-slate-400">{(listing.screenshots || []).length} added — add more</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadImage("screenshots", true)} />
                </label>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 — Validation */}
        {step === 2 && (
          <div className="space-y-5" data-testid="pub-validation">
            <button onClick={runValidation} disabled={busy === "validate"} className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3 rounded-xl font-semibold disabled:opacity-40" data-testid="pub-run-validation">
              {busy === "validate" ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Run pre-submission checks
            </button>
            {validation && (
              <>
                <div className="flex gap-4 font-mono text-sm">
                  <span className="text-emerald-400">{validation.summary.passed} Passed</span>
                  <span className="text-amber-400">{validation.summary.warning} Warning</span>
                  <span className="text-red-400">{validation.summary.action_required} Action Required</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5" data-testid="pub-check-list">
                  {validation.checks.map((c, i) => {
                    const Icon = statusIcon[c.status];
                    return (
                      <div key={i} className="flex items-start gap-3 p-4">
                        <Icon size={17} className={`${statusColor[c.status]} mt-0.5 shrink-0`} />
                        <div>
                          <p className="text-white text-sm font-medium">{c.check}</p>
                          <p className="text-slate-500 text-xs">{c.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!validation.can_submit && <p className="text-red-400 text-sm">Resolve all Action Required items before you can submit.</p>}
              </>
            )}
          </div>
        )}

        {/* STEP 3 — Build */}
        {step === 3 && (
          <div className="space-y-6" data-testid="pub-build">
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => startBuild("android")} disabled={busy === "build"} className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 rounded-xl hover:border-[#0055FF] transition-colors duration-300" data-testid="pub-build-android"><Play size={16} /> Build Android (.aab)</button>
              <button onClick={() => startBuild("ios")} disabled={busy === "build"} className="inline-flex items-center gap-2 border border-white/15 px-5 py-3 rounded-xl hover:border-[#0055FF] transition-colors duration-300" data-testid="pub-build-ios"><Apple size={16} /> Build iOS</button>
            </div>
            {build && (
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="pub-build-progress">
                <p className="font-mono text-xs text-slate-500 mb-4">{build.artifact_type} · {build.platform}</p>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  {(build.stages || ["Preparing","Building","Signing","Validating","Uploading","Complete"]).map((s) => {
                    const stages = build.stages || ["Preparing","Building","Signing","Validating","Uploading","Complete"];
                    const done = stages.indexOf(s) < stages.indexOf(build.stage);
                    const active = s === build.stage;
                    return (
                      <span key={s} className={`font-mono text-[11px] ${active ? "text-[#00F0FF]" : done ? "text-emerald-400" : "text-slate-600"}`}>
                        {active && build.stage !== "Complete" ? "▸ " : done || build.stage === "Complete" ? "✓ " : "○ "}{s}
                      </span>
                    );
                  })}
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#0055FF] to-[#00F0FF] transition-[width] duration-500" style={{ width: `${build.progress}%` }} data-testid="pub-build-bar" />
                </div>
                {build.stage === "Complete" && <p className="text-emerald-400 text-sm mt-4" data-testid="pub-build-complete">Build complete — ready to release.</p>}
              </div>
            )}
          </div>
        )}

        {/* STEP 4 — Release */}
        {step === 4 && (
          <div className="space-y-6" data-testid="pub-release">
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="pub-review">
              <h3 className="font-display font-bold text-lg mb-4">Final review</h3>
              <dl className="grid md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <Row k="App" v={listing.name} /><Row k="Version" v="1.0.0" />
                <Row k="Platform" v={project.platform} /><Row k="Category" v={listing.category} />
                <Row k="Bundle ID" v={listing.bundle_id} /><Row k="Pricing" v={listing.pricing} />
                <Row k="Privacy" v={listing.privacy_url ? "Provided" : "Missing"} />
                <Row k="Countries" v={(listing.countries || []).join(", ")} />
              </dl>
            </div>

            {["ios", "android"].filter((p) => project.platform === "both" || project.platform === p).map((p) => (
              <div key={p} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6">
                <p className="font-display font-bold mb-3 inline-flex items-center gap-2">{p === "ios" ? <><Apple size={16} /> App Store</> : <><Play size={16} /> Google Play</>}</p>
                <div className="flex flex-wrap gap-2">
                  {(p === "ios" ? APPLE_TRACKS : GOOGLE_TRACKS).map((track) => (
                    <button key={track} onClick={() => doSubmit(p, track)} disabled={busy === "submit"}
                      className="inline-flex items-center gap-2 bg-white text-black px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#00F0FF] disabled:opacity-40 transition-colors duration-300"
                      data-testid={`pub-submit-${p}-${track.replace(/\s+/g, "-").toLowerCase()}`}>
                      {busy === "submit" ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />} {track}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {submission && (
              <div className="rounded-2xl border border-[#0055FF]/30 bg-[#0055FF]/10 p-6" data-testid="pub-submission-result">
                <p className="text-[#00F0FF] font-display font-bold">Status: {submission.status}</p>
                <p className="text-slate-400 text-sm mt-1">{submission.platform === "ios" ? "App Store" : "Google Play"} · {submission.track}</p>
              </div>
            )}

            {/* Rejection assistant */}
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-6" data-testid="pub-rejection">
              <h3 className="font-display font-bold text-lg mb-3">AI Rejection Assistant</h3>
              <p className="text-slate-500 text-sm mb-3">Got a rejection? Paste the message and get an explanation + fix plan.</p>
              <textarea value={rejection.message} onChange={(e) => setRejection((x) => ({ ...x, message: e.target.value }))}
                rows={3} placeholder="Paste the App Store / Play rejection message…"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#0055FF] resize-none" data-testid="pub-rejection-input" />
              <button onClick={runRejection} disabled={busy === "reject" || !rejection.message.trim()} className="mt-3 inline-flex items-center gap-2 bg-[#0055FF] text-white px-5 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40" data-testid="pub-rejection-btn">
                {busy === "reject" ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Analyze & fix
              </button>
              {rejection.result && (
                <div className="mt-4 space-y-3 text-sm" data-testid="pub-rejection-result">
                  <p className="text-slate-300"><span className="text-[#00F0FF] font-mono text-xs uppercase">Explanation</span><br />{rejection.result.explanation}</p>
                  <div>
                    <span className="text-[#00F0FF] font-mono text-xs uppercase">Fixes</span>
                    <ul className="mt-1 space-y-1">{(rejection.result.fixes || []).map((f, i) => <li key={i} className="text-slate-300 flex gap-2"><span className="text-[#00F0FF]">▸</span>{f}</li>)}</ul>
                  </div>
                  <p className="text-slate-300"><span className="text-[#00F0FF] font-mono text-xs uppercase">Fix plan</span><br />{rejection.result.fix_plan}</p>
                  <button onClick={() => { setStep(3); toast.info("Rebuild your app, then resubmit."); }} className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 rounded-xl text-xs hover:border-[#0055FF] transition-colors duration-300" data-testid="pub-rebuild-btn"><RefreshCw size={13} /> Rebuild & resubmit</button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-10">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className="inline-flex items-center gap-2 text-slate-400 hover:text-white disabled:opacity-30" data-testid="pub-prev">
            <ArrowLeft size={16} /> Back
          </button>
          {step < STEPS.length - 1 && (
            <button
              onClick={async () => { if (step === 1) await saveListing(); setStep((s) => s + 1); }}
              disabled={!canNext}
              className="inline-flex items-center gap-2 bg-[#0055FF] text-white px-6 py-3 rounded-xl font-semibold disabled:opacity-40 hover:-translate-y-0.5 transition-transform duration-300"
              data-testid="pub-next"
            >
              Next <ArrowRight size={16} />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-white/5 py-1.5">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-white">{v || "—"}</dd>
    </div>
  );
}

function ConnectForm({ provider, onDone }) {
  const [name, setName] = useState("");
  const [cred, setCred] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !cred.trim()) return;
    setBusy(true);
    try {
      await publishingApi.connect({ provider, account_name: name, credential: cred });
      toast.success("Account connected (encrypted)");
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Account / team name" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0055FF]" data-testid={`pub-conn-name-${provider}`} />
      <input value={cred} onChange={(e) => setCred(e.target.value)} type="password" placeholder={provider === "apple" ? "App Store Connect API key (.p8)" : "Play service account JSON"} className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0055FF]" data-testid={`pub-conn-cred-${provider}`} />
      <button onClick={submit} disabled={busy} className="w-full bg-white text-black rounded-lg py-2 text-sm font-semibold hover:bg-[#00F0FF] disabled:opacity-40 transition-colors duration-300" data-testid={`pub-connect-${provider}`}>
        {busy ? "Connecting…" : "Connect securely"}
      </button>
    </div>
  );
}
